import React, { useState, useEffect, useCallback } from 'react';
import {
  GraduationCap, Calendar, Plus, Check, Trash2, Edit2,
  BookOpen, Clock, AlertCircle, Loader2, ChevronDown, X, Settings, PlusCircle
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../api';

const TERM_NAMES = ['First Term', 'Second Term', 'Third Term'];

const STATUS = ({ active }) => active
  ? <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-100 px-2.5 py-0.5 rounded-full"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />Active</span>
  : <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500 bg-slate-100 px-2.5 py-0.5 rounded-full">Inactive</span>;

export default function AcademicsPage() {
  const [years, setYears] = useState([]);
  const [terms, setTerms] = useState([]);
  const [currentSession, setCurrentSession] = useState({ year: null, term: null });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('sessions');
  const [selectedYearFilter, setSelectedYearFilter] = useState('all');

  // Forms
  const [showYearForm, setShowYearForm] = useState(false);
  const [showTermForm, setShowTermForm] = useState(false);
  const [editYear, setEditYear] = useState(null);
  const [editTerm, setEditTerm] = useState(null);
  const [yearForm, setYearForm] = useState({ name: '', start_date: '', end_date: '' });
  const [termForm, setTermForm] = useState({ name: '', academic_year_id: '', start_date: '', end_date: '', days_school_opened: '', term_closes: '', next_term_resumes: '' });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [settingCurrent, setSettingCurrent] = useState(null);

  // Assessment Config state
  const [classes, setClasses] = useState([]);
  const [editConfigClass, setEditConfigClass] = useState(null);
  const [configForm, setConfigForm] = useState([]);
  const [savingConfig, setSavingConfig] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [yearsRes, termsRes, sessionRes, clsRes] = await Promise.all([
        api.get('/academics/years'),
        api.get('/academics/terms'),
        api.get('/academics/current-session'),
        api.get('/academics/classes'),
      ]);
      setYears(yearsRes.data || []);
      setTerms(termsRes.data || []);
      setCurrentSession(sessionRes.data || { year: null, term: null });
      setClasses(clsRes.data || []);
    } catch {
      toast.error('Failed to load academic data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const setCurrent = async (type, id) => {
    setSettingCurrent(id);
    try {
      await api.put(`/academics/${type}s/${id}/set-current`);
      toast.success(`${type === 'year' ? 'Academic year' : 'Term'} set as active`);
      fetchAll();
    } catch (err) {
      toast.error(err?.detail || 'Failed to update');
    } finally {
      setSettingCurrent(null);
    }
  };

  const saveYear = async () => {
    if (!yearForm.name) return toast.error('Year name is required');
    setSaving(true);
    try {
      if (editYear) {
        await api.put(`/academics/years/${editYear.id}`, yearForm);
        toast.success('Academic year updated');
      } else {
        await api.post('/academics/years', { ...yearForm, is_current: false });
        toast.success('Academic year created');
      }
      setShowYearForm(false);
      setEditYear(null);
      setYearForm({ name: '', start_date: '', end_date: '' });
      fetchAll();
    } catch (err) {
      toast.error(err?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const saveTerm = async () => {
    if (!termForm.name || !termForm.academic_year_id) return toast.error('Term name and academic year are required');
    setSaving(true);
    try {
      if (editTerm) {
        await api.put(`/academics/terms/${editTerm.id}`, termForm);
        toast.success('Term updated');
      } else {
        await api.post('/academics/terms', { ...termForm, is_current: false });
        toast.success('Term created');
      }
      setShowTermForm(false);
      setEditTerm(null);
      setTermForm({ name: '', academic_year_id: '', start_date: '', end_date: '', days_school_opened: '', term_closes: '', next_term_resumes: '' });
      fetchAll();
    } catch (err) {
      toast.error(err?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const deleteItem = async (type, id, name) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    setDeleting(id);
    try {
      await api.delete(`/academics/${type}s/${id}`);
      toast.success(`${type === 'year' ? 'Academic year' : 'Term'} deleted`);
      fetchAll();
    } catch (err) {
      toast.error(err?.detail || 'Cannot delete — items may still be linked');
    } finally {
      setDeleting(null);
    }
  };

  const openEditYear = (year) => {
    setEditYear(year);
    setYearForm({ name: year.name, start_date: year.start_date || '', end_date: year.end_date || '' });
    setShowYearForm(true);
  };

  const openEditTerm = (term) => {
    setEditTerm(term);
    setTermForm({
      name: term.name,
      academic_year_id: term.academic_year_id,
      start_date: term.start_date || '',
      end_date: term.end_date || '',
      days_school_opened: term.days_school_opened || '',
      term_closes: term.term_closes || '',
      next_term_resumes: term.next_term_resumes || '',
    });
    setShowTermForm(true);
  };

  const filteredTerms = selectedYearFilter === 'all'
    ? terms
    : terms.filter(t => t.academic_year_id === selectedYearFilter);

  const getYearName = (id) => years.find(y => y.id === id)?.name || id;

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="w-8 h-8 animate-spin text-indigo-400" />
    </div>
  );

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Academic Management</h1>
          <p className="text-sm text-slate-500 mt-0.5">Manage academic years and terms — the active session applies to fees, results, attendance and exams</p>
        </div>
      </div>

      {/* Active Session Banner */}
      <div className="bg-gradient-to-r from-[#516EFA] to-[#7B8EFC] rounded-2xl p-5 text-white" data-testid="active-session-banner">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
            <GraduationCap className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="text-white/70 text-xs font-medium uppercase tracking-wide">Active Session</p>
            <h2 className="text-xl font-bold text-white">
              {currentSession.year?.name || 'No active year'} — {currentSession.term?.name || 'No active term'}
            </h2>
          </div>
        </div>
        <div className="flex gap-4 text-sm text-white/80">
          {currentSession.year && (
            <span className="flex items-center gap-1.5">
              <BookOpen className="w-3.5 h-3.5" />
              {currentSession.year.start_date ? `${currentSession.year.start_date} to ${currentSession.year.end_date}` : currentSession.year.name}
            </span>
          )}
          {currentSession.term && (
            <span className="flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" />
              {currentSession.term.start_date ? `${currentSession.term.start_date} to ${currentSession.term.end_date}` : currentSession.term.name}
            </span>
          )}
        </div>
        {(!currentSession.year || !currentSession.term) && (
          <div className="flex items-center gap-2 mt-3 text-amber-200 text-xs bg-white/10 rounded-lg p-2.5">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            Set an active academic year and term below to enable all modules
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-200 gap-1">
        {[
          { id: 'sessions', label: 'Academic Sessions', count: years.length },
          { id: 'terms', label: 'Terms', count: terms.length },
          { id: 'assessment', label: 'Assessment Config', count: classes.length },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 -mb-px
              ${tab === t.id ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
            {t.label}
            <span className={`text-[11px] px-1.5 py-0.5 rounded-full ${tab === t.id ? 'bg-indigo-100 text-indigo-600' : 'bg-slate-100 text-slate-500'}`}>{t.count}</span>
          </button>
        ))}
      </div>

      {/* Academic Sessions Tab */}
      {tab === 'sessions' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button onClick={() => { setEditYear(null); setYearForm({ name: '', start_date: '', end_date: '' }); setShowYearForm(true); }}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
              data-testid="add-academic-year-btn">
              <Plus className="w-4 h-4" /> New Academic Year
            </button>
          </div>

          {/* Year Form */}
          {showYearForm && (
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm" data-testid="year-form">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-slate-800">{editYear ? 'Edit Academic Year' : 'New Academic Year'}</h3>
                <button onClick={() => { setShowYearForm(false); setEditYear(null); }} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Year Name *</label>
                  <input className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-indigo-400"
                    value={yearForm.name} onChange={e => setYearForm(p => ({ ...p, name: e.target.value }))}
                    placeholder="2025/2026" data-testid="year-name-input" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Start Date</label>
                  <input type="date" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-indigo-400"
                    value={yearForm.start_date} onChange={e => setYearForm(p => ({ ...p, start_date: e.target.value }))} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">End Date</label>
                  <input type="date" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-indigo-400"
                    value={yearForm.end_date} onChange={e => setYearForm(p => ({ ...p, end_date: e.target.value }))} />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-4">
                <button onClick={() => { setShowYearForm(false); setEditYear(null); }} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">Cancel</button>
                <button onClick={saveYear} disabled={saving}
                  className="flex items-center gap-2 px-5 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-60 transition-colors"
                  data-testid="save-year-btn">
                  {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  {editYear ? 'Update' : 'Create Year'}
                </button>
              </div>
            </div>
          )}

          {years.length === 0 ? (
            <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
              <Calendar className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              <p className="font-medium text-slate-600 mb-1">No academic years yet</p>
              <p className="text-xs text-slate-400">Create your first academic session to get started</p>
            </div>
          ) : (
            <div className="space-y-3">
              {years.map(year => {
                const yearTerms = terms.filter(t => t.academic_year_id === year.id);
                const activeTerm = yearTerms.find(t => t.is_current);
                return (
                  <div key={year.id} className={`bg-white rounded-xl border transition-all ${year.is_current ? 'border-indigo-200 shadow-indigo-50 shadow-md' : 'border-slate-200'}`}
                    data-testid={`year-card-${year.name}`}>
                    <div className="flex items-center justify-between p-5">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${year.is_current ? 'bg-indigo-100' : 'bg-slate-100'}`}>
                          <BookOpen className={`w-5 h-5 ${year.is_current ? 'text-indigo-600' : 'text-slate-400'}`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-bold text-slate-900 text-base">{year.name}</h3>
                            <STATUS active={year.is_current} />
                          </div>
                          <p className="text-xs text-slate-400 mt-0.5">
                            {year.start_date && year.end_date ? `${year.start_date} → ${year.end_date}` : 'No dates set'}
                            {' · '}{yearTerms.length} term{yearTerms.length !== 1 ? 's' : ''}
                            {activeTerm ? ` · Active term: ${activeTerm.name}` : ''}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {!year.is_current && (
                          <button onClick={() => setCurrent('year', year.id)} disabled={settingCurrent === year.id}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-colors disabled:opacity-50"
                            data-testid={`set-current-year-${year.name}`}>
                            {settingCurrent === year.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                            Set Active
                          </button>
                        )}
                        <button onClick={() => openEditYear(year)} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        {!year.is_current && (
                          <button onClick={() => deleteItem('year', year.id, year.name)} disabled={deleting === year.id}
                            className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50">
                            {deleting === year.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Inline Terms Preview */}
                    {yearTerms.length > 0 && (
                      <div className="border-t border-slate-100 px-5 py-3 flex flex-wrap gap-2">
                        {yearTerms.map(t => (
                          <span key={t.id} className={`text-xs px-2.5 py-1 rounded-full font-medium ${t.is_current ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-600'}`}>
                            {t.name} {t.is_current && '✓'}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Terms Tab */}
      {tab === 'terms' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-4">
            {/* Filter by year */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-500">Filter:</span>
              <div className="relative">
                <select
                  value={selectedYearFilter}
                  onChange={e => setSelectedYearFilter(e.target.value)}
                  className="border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-indigo-400 pr-8 appearance-none bg-white"
                >
                  <option value="all">All Sessions</option>
                  {years.map(y => <option key={y.id} value={y.id}>{y.name}</option>)}
                </select>
                <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              </div>
            </div>
            <button onClick={() => { setEditTerm(null); setTermForm({ name: '', academic_year_id: currentSession.year?.id || '', start_date: '', end_date: '' }); setShowTermForm(true); }}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
              data-testid="add-term-btn">
              <Plus className="w-4 h-4" /> New Term
            </button>
          </div>

          {/* Term Form */}
          {showTermForm && (
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm" data-testid="term-form">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-slate-800">{editTerm ? 'Edit Term' : 'New Term'}</h3>
                <button onClick={() => { setShowTermForm(false); setEditTerm(null); }} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Term Name *</label>
                  <select className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-indigo-400"
                    value={termForm.name} onChange={e => setTermForm(p => ({ ...p, name: e.target.value }))} data-testid="term-name-select">
                    <option value="">Select term</option>
                    {TERM_NAMES.map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Academic Year *</label>
                  <select className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-indigo-400"
                    value={termForm.academic_year_id} onChange={e => setTermForm(p => ({ ...p, academic_year_id: e.target.value }))}>
                    <option value="">Select year</option>
                    {years.map(y => <option key={y.id} value={y.id}>{y.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Start Date</label>
                  <input type="date" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-indigo-400"
                    value={termForm.start_date} onChange={e => setTermForm(p => ({ ...p, start_date: e.target.value }))} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">End Date</label>
                  <input type="date" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-indigo-400"
                    value={termForm.end_date} onChange={e => setTermForm(p => ({ ...p, end_date: e.target.value }))} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Days School Opened</label>
                  <input type="number" min="1" max="200" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-indigo-400"
                    value={termForm.days_school_opened} onChange={e => setTermForm(p => ({ ...p, days_school_opened: e.target.value }))}
                    placeholder="e.g. 90" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Term Closes (Date)</label>
                  <input type="text" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-indigo-400"
                    value={termForm.term_closes} onChange={e => setTermForm(p => ({ ...p, term_closes: e.target.value }))}
                    placeholder="e.g. 2024-12-20" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-slate-700 mb-1">Next Term Resumes (Date)</label>
                  <input type="text" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-indigo-400"
                    value={termForm.next_term_resumes} onChange={e => setTermForm(p => ({ ...p, next_term_resumes: e.target.value }))}
                    placeholder="e.g. 2025-01-13" />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-4">
                <button onClick={() => { setShowTermForm(false); setEditTerm(null); }} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">Cancel</button>
                <button onClick={saveTerm} disabled={saving}
                  className="flex items-center gap-2 px-5 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-60 transition-colors"
                  data-testid="save-term-btn">
                  {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  {editTerm ? 'Update' : 'Create Term'}
                </button>
              </div>
            </div>
          )}

          {filteredTerms.length === 0 ? (
            <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
              <Calendar className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              <p className="font-medium text-slate-600 mb-1">No terms found</p>
              <p className="text-xs text-slate-400">Create terms for your academic sessions</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50">
                    {['Term', 'Academic Year', 'Start Date', 'End Date', 'Status', ''].map(h => (
                      <th key={h} className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredTerms.map(term => (
                    <tr key={term.id} className="border-b border-slate-50 hover:bg-slate-50 transition-colors" data-testid={`term-row-${term.name}`}>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${term.is_current ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                          <span className="text-sm font-semibold text-slate-800">{term.name}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3.5 text-sm text-slate-500">{getYearName(term.academic_year_id)}</td>
                      <td className="px-5 py-3.5 text-sm text-slate-500">{term.start_date || '—'}</td>
                      <td className="px-5 py-3.5 text-sm text-slate-500">{term.end_date || '—'}</td>
                      <td className="px-5 py-3.5"><STATUS active={term.is_current} /></td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-1 justify-end">
                          {!term.is_current && (
                            <button onClick={() => setCurrent('term', term.id)} disabled={settingCurrent === term.id}
                              className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-colors disabled:opacity-50"
                              data-testid={`set-active-term-${term.name.replace(' ', '-')}`}>
                              {settingCurrent === term.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Set Active
                            </button>
                          )}
                          <button onClick={() => openEditTerm(term)} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg">
                            <Edit2 className="w-3.5 h-3.5" />
                          </button>
                          {!term.is_current && (
                            <button onClick={() => deleteItem('term', term.id, term.name)} disabled={deleting === term.id}
                              className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg disabled:opacity-50">
                              {deleting === term.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      {/* Assessment Config Tab */}
      {tab === 'assessment' && (
        <AssessmentConfigTab
          classes={classes}
          editConfigClass={editConfigClass}
          setEditConfigClass={setEditConfigClass}
          configForm={configForm}
          setConfigForm={setConfigForm}
          savingConfig={savingConfig}
          setSavingConfig={setSavingConfig}
          onSaved={fetchAll}
        />
      )}
    </div>
  );
}

function AssessmentConfigTab({ classes, editConfigClass, setEditConfigClass, configForm, setConfigForm, savingConfig, setSavingConfig, onSaved }) {
  const openEdit = (cls) => {
    setEditConfigClass(cls);
    const config = cls.assessment_config || [
      { name: '1st CA', max_score: 20 },
      { name: '2nd CA', max_score: 20 },
      { name: 'Exam', max_score: 60 },
    ];
    setConfigForm(config.map(c => ({ ...c })));
  };

  const saveConfig = async () => {
    if (!editConfigClass) return;
    if (configForm.some(c => !c.name || !c.max_score)) {
      toast.error('All components need a name and max score'); return;
    }
    setSavingConfig(true);
    try {
      await api.put(`/academics/classes/${editConfigClass.id}/assessment-config`, { components: configForm });
      toast.success('Assessment config saved');
      onSaved();
      setEditConfigClass(null);
    } catch (err) {
      toast.error(err?.detail || 'Failed to save');
    } finally {
      setSavingConfig(false);
    }
  };

  const addComponent = () => setConfigForm(prev => [...prev, { name: '', max_score: 20 }]);
  const removeComponent = (i) => setConfigForm(prev => prev.filter((_, idx) => idx !== i));
  const updateComp = (i, field, value) => setConfigForm(prev => prev.map((c, idx) => idx === i ? { ...c, [field]: field === 'max_score' ? Number(value) : value } : c));

  const total = configForm.reduce((s, c) => s + (Number(c.max_score) || 0), 0);

  return (
    <div className="space-y-4">
      <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-4 text-sm text-indigo-700 flex items-start gap-2">
        <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <span>Configure score components per class (e.g. 1st CA: 20 marks, 2nd CA: 20 marks, Exam: 60 marks). These columns appear on report cards.</span>
      </div>

      {editConfigClass && (
        <div className="bg-white rounded-xl border border-indigo-200 p-5 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-800 text-sm">Edit Assessment Config — <span className="text-indigo-600">{editConfigClass.name}</span></h3>
            <button onClick={() => setEditConfigClass(null)} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
          </div>
          <div className="space-y-2 mb-4">
            {configForm.map((comp, i) => (
              <div key={i} className="flex items-center gap-3">
                <input value={comp.name} onChange={e => updateComp(i, 'name', e.target.value)}
                  className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-indigo-400"
                  placeholder="Component name (e.g. 1st CA)" />
                <input type="number" value={comp.max_score} onChange={e => updateComp(i, 'max_score', e.target.value)}
                  className="w-24 border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-indigo-400"
                  placeholder="Max" min="1" max="200" />
                <span className="text-xs text-slate-400">marks</span>
                <button onClick={() => removeComponent(i)} className="text-slate-400 hover:text-red-500">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button onClick={addComponent} className="flex items-center gap-1.5 text-xs text-indigo-600 hover:text-indigo-800 font-medium">
                <PlusCircle className="w-3.5 h-3.5" /> Add Component
              </button>
              <span className={`text-xs font-semibold ${total === 100 ? 'text-green-600' : 'text-amber-600'}`}>
                Total: {total}/100 {total !== 100 && '(should be 100)'}
              </span>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setEditConfigClass(null)} className="px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
              <button onClick={saveConfig} disabled={savingConfig}
                className="flex items-center gap-1.5 px-4 py-1.5 bg-indigo-600 text-white text-xs font-semibold rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                data-testid="save-config-btn">
                {savingConfig ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                Save Config
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {classes.map(cls => {
          const config = cls.assessment_config || [
            { name: '1st CA', max_score: 20 },
            { name: '2nd CA', max_score: 20 },
            { name: 'Exam', max_score: 60 },
          ];
          return (
            <div key={cls.id} className="bg-white rounded-xl border border-slate-200 p-4 flex items-center justify-between">
              <div>
                <p className="font-bold text-slate-800 text-sm">{cls.name}</p>
                <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                  {config.map((c, i) => (
                    <span key={i} className="text-xs bg-slate-100 text-slate-600 px-2.5 py-1 rounded-full font-medium">
                      {c.name}: {c.max_score}
                    </span>
                  ))}
                  <span className="text-xs text-slate-400">= {config.reduce((s, c) => s + c.max_score, 0)} total</span>
                </div>
              </div>
              <button onClick={() => openEdit(cls)}
                className="flex items-center gap-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-800 bg-indigo-50 hover:bg-indigo-100 px-3 py-1.5 rounded-lg transition-colors"
                data-testid={`edit-config-${cls.name}`}>
                <Settings className="w-3.5 h-3.5" /> Configure
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
